import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import { Link, withRouter } from 'react-router-dom';

import { useSelector, connect } from 'react-redux';

import { openModal } from 'mastodon/actions/modal';
import { fetchServer } from 'mastodon/actions/server';
import { Avatar } from 'mastodon/components/avatar';
import { Icon } from 'mastodon/components/icon';
import { IconWithBadge } from 'mastodon/components/icon_with_badge';
import { WordmarkLogo, SymbolLogo } from 'mastodon/components/logo';
import {
  identityContextPropShape,
  withIdentity,
} from 'mastodon/identity_context';
import { registrationsOpen, me, sso_redirect } from 'mastodon/initial_state';
import { selectUnreadNotificationGroupsCount } from 'mastodon/selectors/notifications';

import ColumnLink from './column_link';
import { useSidebar } from 'mastodon/components/use-sidebar';
import { BackButton } from 'mastodon/components/back_button';
import { HomeButton } from 'mastodon/components/home_button';
import { SearchIcon } from 'mastodon/components/icons/search';
import { ExploreIcon } from 'mastodon/components/icons/explore';
import { NotificationIcon } from 'mastodon/components/icons/notification';
import { MenuIcon } from 'mastodon/components/icons/menu';
import { isMobile } from 'mastodon/is_mobile';


const messages = defineMessages({
  toggleSidebar: { id: 'column_header.toggle_sidebar', defaultMessage: 'Toggle sidebar' },
});

const useScrollDirection = () => {
  const [scrollDirection, setScrollDirection] = React.useState(null);
  const [lastScrollY, setLastScrollY] = React.useState(0);
  const [isMobileDevice, setIsMobileDevice] = React.useState(isMobile(window.innerWidth));


  React.useEffect(() => {
    const handleResize = () => {
      setIsMobileDevice(isMobile(window.innerWidth));
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  React.useEffect(() => {
    if (!isMobileDevice) {
      setScrollDirection(null);
      return;
    }

    const updateScrollDirection = () => {
      const scrollY = window.pageYOffset;
      const direction = scrollY > lastScrollY ? 'down' : 'up';

      if (direction !== scrollDirection && Math.abs(scrollY - lastScrollY) > 10) {
        setScrollDirection(direction);
      }
      setLastScrollY(scrollY > 0 ? scrollY : 0);
    };

    const throttledUpdateScrollDirection = throttle(updateScrollDirection, 100);
    window.addEventListener('scroll', throttledUpdateScrollDirection, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledUpdateScrollDirection);
    };
  }, [scrollDirection, lastScrollY, isMobileDevice]);

  return scrollDirection;
};

const throttle = (func, limit) => {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

const HeaderWrapper = (props) => {
  const scrollDirection = useScrollDirection();
  const [isVisible, setIsVisible] = React.useState(true);
  const { isTransitioning, toggleSidebar } = useSidebar();

  React.useEffect(() => {
    if (scrollDirection === "down") {
      setIsVisible(false);
    } else if (scrollDirection === "up") {
      setIsVisible(true);
    }else {
      setIsVisible(true);
    }
  }, [scrollDirection]);

  const handleSidebarToggle = React.useCallback(
    (e) => {
      e.stopPropagation();
      toggleSidebar();
    },
    [toggleSidebar],
  );

  return <Header {...props} isTransitioning={isTransitioning} handleSidebarToggle={handleSidebarToggle} isVisible={isVisible} />;
};

const Account = connect((state) => ({
  account: state.getIn(['accounts', me]),
}))(({ account }) => (
  <Link to={`/@${account.get('acct')}`} title={account.get('acct')} className='avatar-link__header'>
    <Avatar account={account} size={35} />
  </Link>
));

const NotificationsLink = () => {
  const count = useSelector(selectUnreadNotificationGroupsCount);

  const NotificationActiveIcon = () => <NotificationIcon className='icon-active' />;

  return (
    <ColumnLink
      key='notifications'
      transparent
      to='/notifications'
      icon={
        <IconWithBadge
          id='bell'
          icon={NotificationIcon}
          count={count}
          className='column-link__icon'
        />
      }
      activeIcon={
        <IconWithBadge
          id='bell'
          icon={NotificationActiveIcon}
          count={count}
          className='column-link__icon'
        />
      }
    />
  );
};


const ExploreLink = () => (
  <ColumnLink
    transparent
    to='/explore'
    icon='explore'
    iconComponent={ExploreIcon}
  />
);

const SearchLink = () => (
  <ColumnLink
    transparent
    to='/search'
    icon='search'
    iconComponent={SearchIcon}
  />
);

const mapStateToProps = (state) => ({
  signupUrl:
    state.getIn(['server', 'server', 'registrations', 'url'], null) ||
    '/auth/sign_up',
});

const mapDispatchToProps = (dispatch) => ({
  openClosedRegistrationsModal() {
    dispatch(openModal({ modalType: 'CLOSED_REGISTRATIONS' }));
  },
  dispatchServer() {
    dispatch(fetchServer());
  },
});

class Header extends PureComponent {
  static propTypes = {
    identity: identityContextPropShape,
    openClosedRegistrationsModal: PropTypes.func,
    location: PropTypes.object,
    signupUrl: PropTypes.string.isRequired,
    dispatchServer: PropTypes.func,
    intl: PropTypes.object.isRequired,
    isVisible: PropTypes.bool,
    isTransitioning: PropTypes.bool,
    handleSidebarToggle: PropTypes.func
  };

  componentDidMount() {
    const { dispatchServer } = this.props;
    dispatchServer();
  }

  isShowLogo = () => {
    const { location } = this.props;
    return location.pathname.startsWith('/publish');
  };

  render() {
    const { signedIn } = this.props.identity;
    const { location, openClosedRegistrationsModal, signupUrl, intl, isVisible, isTransitioning, handleSidebarToggle } =
      this.props;

    if (location.pathname.startsWith('/@') && window.innerWidth <= 630) {
      return null;
    }

    let content;

    if (signedIn) {
      content = (
        <div className='ui__header__btn'>
          <SearchLink />
          {signedIn && <NotificationsLink />}
          {location.pathname !== '/publish' && (
            <Link
              to='/publish'
              className='button button-secondary publish-button'
            >
              <FormattedMessage
                id='compose_form.publish_form'
                defaultMessage='New post'
              />
            </Link>
          )}
          {/* <Account /> */}
          {signedIn && (
            <button
                onClick={handleSidebarToggle}
                className='column-header__sidebar-toggle'
                title={intl.formatMessage(messages.toggleSidebar)}
                aria-label={intl.formatMessage(messages.toggleSidebar)}
                disabled={isTransitioning}
              >
              <MenuIcon />
            </button>
          )}
        </div>
      );
    } else {
      if (sso_redirect) {
        content = (
          <a
            href={sso_redirect}
            data-method='post'
            className='button button--block button-tertiary'
          >
            <FormattedMessage
              id='sign_in_banner.sso_redirect'
              defaultMessage='Login or Register'
            />
          </a>
        );
      } else {
        let signupButton;

        if (registrationsOpen) {
          signupButton = (
            <a href={signupUrl} className='button'>
              <FormattedMessage
                id='sign_in_banner.create_account'
                defaultMessage='Create account'
              />
            </a>
          );
        } else {
          signupButton = (
            <button className='button' onClick={openClosedRegistrationsModal}>
              <FormattedMessage
                id='sign_in_banner.create_account'
                defaultMessage='Create account'
              />
            </button>
          );
        }

        content = (
          <>
            {/* {signupButton} */}
            <a href='/auth/laoid' className='button button-tertiary btn-sso'>
              <FormattedMessage
                id='sign_in_banner.sign_in'
                defaultMessage='Login'
              />
            </a>
          </>
        );
      }
    }

    const headerStyle = {
      flexDirection: this.isShowLogo() ? "row" : "",
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      transform: isVisible ? "translateY(0)" : "translateY(-100%)",
      opacity: isVisible ? 1 : 0,
      transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
      border: "none"
    };

    const isBackButton = location.pathname.match(/^\/@[^/]+\/[^/]+$/);

    return (
      <div
        style={headerStyle}
        className='ui__header ui__header--sticky'
      >
        {this.isShowLogo() && (
          <Link to='/' className='ui__header__logo'>
            <WordmarkLogo />
            <SymbolLogo />
          </Link>
        )}

        <div className='ui__header__links'>
          {isBackButton ? <BackButton style='header-back-button' /> : <HomeButton /> }
          {content}
        </div>
      </div>
    );
  }
}

export default injectIntl(withRouter(withIdentity(connect(mapStateToProps, mapDispatchToProps)(HeaderWrapper))));
